import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ChevronLeft, Settings, Clock, Mail, MessageSquare, Shield, Bot, Save, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AutomationSettingsPanel = ({ onBack }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/automation-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/automation-settings`, settings);
      toast.success('Settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Automation Settings
            </h1>
            <p className="text-sm text-muted-foreground">Global configuration for all automations</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Save className="h-4 w-4 mr-2" /> Save Settings</>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Business Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Business Hours
            </CardTitle>
            <CardDescription>
              Define when automations can run time-based triggers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={settings?.business_hours_start || '09:00'}
                  onChange={(e) => updateSetting('business_hours_start', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={settings?.business_hours_end || '17:00'}
                  onChange={(e) => updateSetting('business_hours_end', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Timezone</Label>
              <Select
                value={settings?.timezone || 'America/New_York'}
                onValueChange={(val) => updateSetting('timezone', val)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern Time</SelectItem>
                  <SelectItem value="America/Chicago">Central Time</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Business Days</Label>
              <div className="flex gap-2 mt-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                  const isActive = settings?.business_days?.includes(idx);
                  return (
                    <Button
                      key={day}
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      className="w-10"
                      onClick={() => {
                        const current = settings?.business_days || [0, 1, 2, 3, 4];
                        const updated = isActive
                          ? current.filter(d => d !== idx)
                          : [...current, idx];
                        updateSetting('business_days', updated);
                      }}
                    >
                      {day.charAt(0)}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rate Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Rate Limits
            </CardTitle>
            <CardDescription>
              Prevent excessive messaging
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  SMS Hourly Limit
                </Label>
                <Input
                  type="number"
                  value={settings?.sms_hourly_limit || 100}
                  onChange={(e) => updateSetting('sms_hourly_limit', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  SMS Daily Limit
                </Label>
                <Input
                  type="number"
                  value={settings?.sms_daily_limit || 1000}
                  onChange={(e) => updateSetting('sms_daily_limit', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Hourly Limit
                </Label>
                <Input
                  type="number"
                  value={settings?.email_hourly_limit || 200}
                  onChange={(e) => updateSetting('email_hourly_limit', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Daily Limit
                </Label>
                <Input
                  type="number"
                  value={settings?.email_daily_limit || 2000}
                  onChange={(e) => updateSetting('email_daily_limit', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Retry Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Retry Settings</CardTitle>
            <CardDescription>
              Configure default retry behavior for failed actions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Default Retry Attempts</Label>
                <Input
                  type="number"
                  value={settings?.default_retry_attempts || 3}
                  onChange={(e) => updateSetting('default_retry_attempts', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Retry Delay (minutes)</Label>
                <Input
                  type="number"
                  value={settings?.default_retry_delay_minutes || 5}
                  onChange={(e) => updateSetting('default_retry_delay_minutes', parseInt(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Notify on Failure</Label>
                <p className="text-xs text-muted-foreground">Send alert when automation fails</p>
              </div>
              <Switch
                checked={settings?.notify_on_failure ?? true}
                onCheckedChange={(val) => updateSetting('notify_on_failure', val)}
              />
            </div>
          </CardContent>
        </Card>

        {/* AI Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="h-5 w-5" />
              AI Settings
            </CardTitle>
            <CardDescription>
              Configure AI-powered features
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Require Approval</Label>
                <p className="text-xs text-muted-foreground">AI-generated content needs admin approval</p>
              </div>
              <Switch
                checked={settings?.ai_require_approval ?? true}
                onCheckedChange={(val) => updateSetting('ai_require_approval', val)}
              />
            </div>
            <div>
              <Label>AI Model</Label>
              <Select
                value={settings?.ai_model || 'gpt-4o'}
                onValueChange={(val) => updateSetting('ai_model', val)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o">GPT-4o (Recommended)</SelectItem>
                  <SelectItem value="gpt-4o-mini">GPT-4o Mini (Faster)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Sandbox Mode */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Sandbox Mode</CardTitle>
            <CardDescription>
              Test automations without sending real messages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Sandbox Mode</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, messages are logged but not actually sent
                </p>
              </div>
              <Switch
                checked={settings?.sandbox_mode_enabled ?? false}
                onCheckedChange={(val) => updateSetting('sandbox_mode_enabled', val)}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
